import axios from 'axios'
import { updatePermissions } from '../store/userSlice'
import { extractModuleAccessFromPayload } from './moduleAccess'
import { LOOKUP_API_ACTIONS } from './lookupAccess'

const API_URL = process.env.REACT_APP_API_URL

function safeParse(raw) {
  if (raw == null) return null
  if (typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Lookup list responses (suppliers/customers/users) must never update
 * permissions / moduleAccess — only login + validate-session (+ role socket events).
 */
export function isLookupDataPayload(data) {
  if (!data || typeof data !== 'object') return false
  const action = String(data.action || data.type || '').toLowerCase()
  if (LOOKUP_API_ACTIONS.has(action)) return true
  // Shape heuristics for list APIs that omit action on the response body
  if (Array.isArray(data.suppliers) || Array.isArray(data.data?.suppliers)) {
    return !Array.isArray(data.permissions) && !Array.isArray(data.moduleAccess)
  }
  if (Array.isArray(data.customers) || Array.isArray(data.data?.customers)) {
    return !Array.isArray(data.permissions) && !Array.isArray(data.moduleAccess)
  }
  if (Array.isArray(data.users) || Array.isArray(data.data?.users)) {
    return !Array.isArray(data.permissions) && !Array.isArray(data.moduleAccess)
  }
  if (
    Array.isArray(data.salesOrders) ||
    Array.isArray(data.data?.salesOrders) ||
    Array.isArray(data.quotes) ||
    Array.isArray(data.data?.quotes)
  ) {
    return !Array.isArray(data.permissions) && !Array.isArray(data.moduleAccess)
  }
  return false
}

export function parsePermissionsFromSocketPayload(data) {
  if (!data || typeof data !== 'object') return null
  if (isLookupDataPayload(data)) return null

  const permissions =
    data.permissions ||
    data.data?.permissions ||
    data.payload?.permissions ||
    data.user?.permissions

  const role = data.role || data.data?.role || data.user?.role
  const moduleAccess =
    data.moduleAccess ||
    data.data?.moduleAccess ||
    data.user?.moduleAccess ||
    data.payload?.moduleAccess

  if (!Array.isArray(permissions)) return null

  return {
    permissions,
    role,
    ...(Array.isArray(moduleAccess) ? { moduleAccess } : {}),
  }
}

export function isPermissionsSocketEvent(data) {
  if (!data || typeof data !== 'object') return false

  const type = String(data.type || data.action || data.event || '').toLowerCase()

  if (
    type === 'permissions-updated' ||
    type === 'permission-updated' ||
    type === 'role-permissions-updated' ||
    type === 'role_updated' ||
    type === 'role-updated' ||
    type === 'update-role' ||
    type === 'user-permissions-updated'
  ) {
    return true
  }

  return Boolean(
    parsePermissionsFromSocketPayload(data) &&
      (type.includes('permission') || type.includes('role'))
  )
}

export function applyPermissionsUpdate(
  dispatch,
  { permissions, role, moduleAccess },
  sourcePayload,
) {
  // Never promote lookup API data into auth state
  if (sourcePayload && isLookupDataPayload(sourcePayload)) return false

  if (!Array.isArray(permissions) && !Array.isArray(moduleAccess) && !role) {
    return false
  }

  dispatch(
    updatePermissions({
      ...(Array.isArray(permissions) ? { permissions } : {}),
      ...(role ? { role } : {}),
      ...(Array.isArray(moduleAccess) ? { moduleAccess } : {}),
    }),
  )

  window.dispatchEvent(
    new CustomEvent('scm:permissions-updated', {
      detail: {
        permissions:
          permissions ||
          JSON.parse(localStorage.getItem('permissions') || '[]'),
        role: role || localStorage.getItem('role'),
        moduleAccess:
          moduleAccess ||
          JSON.parse(localStorage.getItem('moduleAccess') || '[]'),
      },
    }),
  )

  return true
}

/**
 * Pull latest role / permissions / moduleAccess via validate-session.
 * @returns {'updated'|'unchanged'|'logout'|'error'}
 */
export async function refreshSessionAccess(dispatch, { silent } = {}) {
  const userId = localStorage.getItem('id')
  if (!userId) return 'error'

  try {
    const response = await axios.post(API_URL, {
      action: 'validate-session',
      currentUserId: userId,
    })

    const data = safeParse(response.data)
    if (!data) return 'error'

    if (data.forceLogout) {
      return 'logout'
    }

    if (!data.success || !data.data) return 'unchanged'

    const latestRole = data.data.role
    const latestPermissions = Array.isArray(data.data.permissions)
      ? data.data.permissions
      : null
    const hasModuleAccessField =
      Array.isArray(data.data.moduleAccess) ||
      Array.isArray(data.data.user?.moduleAccess)
    const latestModuleAccess = hasModuleAccessField
      ? extractModuleAccessFromPayload(data.data)
      : null

    const currentRole = localStorage.getItem('role')
    const currentPermissions = JSON.parse(
      localStorage.getItem('permissions') || '[]',
    )
    const currentModuleAccess = JSON.parse(
      localStorage.getItem('moduleAccess') || '[]',
    )

    const permissionChanged =
      latestPermissions != null &&
      JSON.stringify([...currentPermissions].sort()) !==
        JSON.stringify([...latestPermissions].sort())

    const moduleAccessChanged =
      latestModuleAccess != null &&
      JSON.stringify(currentModuleAccess) !==
        JSON.stringify(latestModuleAccess)

    const roleChanged = Boolean(latestRole) && currentRole !== latestRole

    if (permissionChanged || roleChanged || moduleAccessChanged) {
      applyPermissionsUpdate(dispatch, {
        ...(latestRole ? { role: latestRole } : {}),
        ...(latestPermissions != null ? { permissions: latestPermissions } : {}),
        ...(latestModuleAccess != null
          ? { moduleAccess: latestModuleAccess }
          : {}),
      })
      return 'updated'
    }

    return 'unchanged'
  } catch (error) {
    const parsed = safeParse(error.response?.data)
    if (parsed?.forceLogout) return 'logout'
    if (!silent) {
      console.error('Failed to refresh session access:', error)
    }
    return 'error'
  }
}

export async function refreshPermissionsFromApi(dispatch) {
  const userId = localStorage.getItem('id')
  if (!userId) return false

  try {
    const response = await axios.post(API_URL, {
      action: 'get-user',
      id: userId,
    })

    let responseData = response.data
    if (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData)
      } catch {
        responseData = response.data
      }
    }

    const user = responseData.user || responseData.data
    if (responseData.success && user && Array.isArray(user.permissions)) {
      applyPermissionsUpdate(dispatch, {
        permissions: user.permissions,
        role: user.role,
        moduleAccess: extractModuleAccessFromPayload(user) ||
          extractModuleAccessFromPayload(responseData),
      })
      return true
    }
  } catch (error) {
    console.error('Failed to refresh permissions from API:', error)
  }

  return false
}

export async function handlePermissionsSocketMessage(dispatch, data) {
  if (isLookupDataPayload(data)) return false
  if (!isPermissionsSocketEvent(data)) return false

  const parsed = parsePermissionsFromSocketPayload(data)
  if (parsed) {
    applyPermissionsUpdate(dispatch, parsed, data)
    return true
  }

  return refreshPermissionsFromApi(dispatch)
}
